/**
 * Iteration 3 feature screens owned by other agents (A4 graph, A5 logs, A6 account security).
 *
 * Each entry is located with import.meta.glob, so a missing file is simply "not available":
 * no route, no nav link, and the app still builds. Present files are code-split and loaded on
 * first visit. Contract for feature owners: the entry file exports the page component either as
 * `default` or under the name `exportName` (e.g. `export function MessageLogPage`).
 */
import { createElement, lazy, type ComponentType, type LazyExoticComponent } from "react";
import { EmptyState } from "@/ui";

type PageModule = Record<string, unknown>;
type Loader = () => Promise<PageModule>;

export interface FeatureRoute {
  id: string;
  /** Route path under the authenticated shell (no leading slash). */
  path: string;
  navLabel: string;
  /** Nav group: placed after this core nav path. */
  after: string;
  /** Named export to use when the module has no default export. */
  exportName: string;
  Page: LazyExoticComponent<ComponentType>;
}

// Patterns must be string literals for Vite to resolve them at build time.
const graph = import.meta.glob<PageModule>("../features/graph/MappingGraph.tsx");
const messageLog = import.meta.glob<PageModule>("../features/logs/MessageLogPage.tsx");
const auditLog = import.meta.glob<PageModule>("../features/logs/AuditLogPage.tsx");
const accountSecurity = import.meta.glob<PageModule>("../features/account/AccountSecurityPage.tsx");

function missingExport(file: string, name: string): ComponentType {
  return function MissingExport() {
    return createElement(EmptyState, {
      title: "Screen not available",
      description: `${file} has no default export or \`${name}\` export.`,
    });
  };
}

function pickPage(m: PageModule, file: string, name: string): ComponentType {
  const page = m.default ?? m[name];
  // Plain function components, or memo/forwardRef objects (which carry $$typeof).
  const isComponent =
    typeof page === "function" || (typeof page === "object" && page !== null && "$$typeof" in page);
  return isComponent ? (page as ComponentType) : missingExport(file, name);
}

function firstLoader(modules: Record<string, Loader>): Loader | undefined {
  return Object.values(modules)[0];
}

function define(
  modules: Record<string, Loader>,
  meta: Omit<FeatureRoute, "Page">,
): FeatureRoute | null {
  const file = Object.keys(modules)[0];
  const load = firstLoader(modules);
  if (!file || !load) return null;
  const Page = lazy(() =>
    load().then((m) => ({ default: pickPage(m, file, meta.exportName) })),
  );
  return { ...meta, Page };
}

export const FEATURE_ROUTES: readonly FeatureRoute[] = [
  define(graph, {
    id: "graph",
    path: "graph",
    navLabel: "Graph",
    after: "/mappings",
    exportName: "MappingGraph",
  }),
  define(messageLog, {
    id: "message-log",
    path: "logs/messages",
    navLabel: "Message log",
    after: "/dead-letters",
    exportName: "MessageLogPage",
  }),
  define(auditLog, {
    id: "audit-log",
    path: "logs/audit",
    navLabel: "Audit log",
    after: "/dead-letters",
    exportName: "AuditLogPage",
  }),
  define(accountSecurity, {
    id: "account-security",
    path: "account",
    navLabel: "Account security",
    after: "/accounts",
    exportName: "AccountSecurityPage",
  }),
].filter((f): f is FeatureRoute => f !== null);
